import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { getBlogCategoryLabelFromBlog } from "./blogCategoryOptions";
import "./BlogRichTextEditor.css";

const getTags = (tags) => {
  const tagList = Array.isArray(tags) ? tags : String(tags || "").split(",");

  return tagList.map((tag) => String(tag).trim()).filter(Boolean);
};

const formatBlogDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const decodeHtmlEntities = (value) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = String(value || "");
  return textarea.value;
};

const getPlainTextFromHtml = (html = "") =>
  decodeHtmlEntities(
    String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );

const createAnchorId = (text, usedIds) => {
  const baseId =
    String(text || "")
      .toLowerCase()
      .trim()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section";

  let id = baseId;
  let count = 2;

  while (usedIds.has(id)) {
    id = `${baseId}-${count}`;
    count += 1;
  }

  usedIds.add(id);
  return id;
};

const prepareBlogContentSections = (contentHtml = "") => {
  const usedIds = new Set();
  const sections = [];
  const headingRegex = /<h([23])\b([^>]*)>([\s\S]*?)<\/h\1>/gi;
  const contentHtmlWithAnchors = String(contentHtml || "").replace(
    headingRegex,
    (headingHtml, level, attributes = "", innerHtml = "") => {
      const text = getPlainTextFromHtml(innerHtml);
      if (!text) return headingHtml;

      const existingId = attributes.match(/\sid=(["'])(.*?)\1/i)?.[2];
      const id = existingId && !usedIds.has(existingId) ? existingId : createAnchorId(text, usedIds);
      usedIds.add(id);

      sections.push({
        id,
        text,
        level: Number(level),
      });

      const nextAttributes = attributes.replace(/\sid=(["']).*?\1/i, "");
      return `<h${level}${nextAttributes} id="${id}">${innerHtml}</h${level}>`;
    },
  );

  return {
    contentHtml: contentHtmlWithAnchors,
    sections,
  };
};

const BlogSubsectionSummary = ({ sections }) => {
  if (!sections || sections.length < 2) return null;

  return (
    <nav className="blog-subsection-summary" aria-label="Article sections">
      <p>In this article</p>
      <ul>
        {sections.map((section) => (
          <li key={section.id} className={section.level === 3 ? "is-nested" : ""}>
            <a href={`#${section.id}`}>{section.text}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const BlogContentPreview = ({ blog, coverImage, categoryOptions }) => {
  const preparedContent = useMemo(
    () => prepareBlogContentSections(blog?.contentHtml || ""),
    [blog?.contentHtml],
  );
  const sanitizedContent = useMemo(
    () =>
      DOMPurify.sanitize(preparedContent.contentHtml || "", {
        ADD_ATTR: [
          "style",
          "target",
          "rel",
          "align",
          "width",
          "height",
          "class",
          "id",
          "cols",
          "index",
          "data-column-width-percent",
        ],
      }),
    [preparedContent.contentHtml],
  );

  const tags = useMemo(() => getTags(blog?.tags), [blog?.tags]);
  const publishedDate = formatBlogDate(blog?.publishedAt || blog?.createdAt);

  return (
    <section className="blog-client-preview">
      <article>
        <section className="blog-client-preview-heading">
          <div className="blog-client-preview-meta">
            {getBlogCategoryLabelFromBlog(blog, "Blog", categoryOptions)}
          </div>
          <h1 className="blog-client-preview-title">
            {blog?.title || "Blog title preview"}
          </h1>

          {blog?.excerpt && (
            <p className="blog-client-preview-excerpt">{blog.excerpt}</p>
          )}

          {publishedDate && (
            <p className="blog-client-preview-date">{publishedDate}</p>
          )}

          <BlogSubsectionSummary sections={preparedContent.sections} />
        </section>

        {coverImage && (
          <section className="blog-client-preview-cover-section">
            <div className="blog-client-preview-cover-frame">
              <img
                className="blog-client-preview-cover"
                src={coverImage}
                alt={blog?.coverAlt || blog?.title || "Blog cover"}
              />
            </div>
          </section>
        )}

        <section className="blog-client-preview-content">
          <div
            className="blog-rich-content"
            dangerouslySetInnerHTML={{
              __html:
                sanitizedContent ||
                "<p>Start writing content to preview the client blog rendering.</p>",
            }}
          />

          {tags.length > 0 && (
            <div className="blog-client-preview-tags">
              {tags.map((tag, index) => (
                <span key={`${tag}-${index}`}>{tag}</span>
              ))}
            </div>
          )}
        </section>
      </article>
    </section>
  );
};

export default BlogContentPreview;
